import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../shared/api';
import { useRealtimeEvents } from '../../../../shared/useRealtimeEvents';

export const BUNDLES_API = 'projects/api/project-bundles/';

// Changes of these models can change what is packed or what is left to pack.
const REALTIME_MODELS = new Set([
  'projects.projectbundle',
  'projects.projectbundlecontent',
  'projects.projectequipment',
  'projects.projectallocation',
]);

export const packingQueryKey = (projectId) => ['project-packing', String(projectId)];

/**
 * Packing overview of a project (cases, loose units, loose quantities, summary) and the actions on it.
 * Every action returns a promise that rejects with the API error, so callers can show its message.
 */
const usePacking = (projectId) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: packingQueryKey(projectId),
    queryFn: () => api.get(`${BUNDLES_API}packing/?project=${projectId}`),
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: packingQueryKey(projectId) }),
    [queryClient, projectId],
  );

  const debounceRef = useRef(null);
  useEffect(() => () => clearTimeout(debounceRef.current), []);
  const onRealtimeEvent = useCallback((event) => {
    if (!REALTIME_MODELS.has(event.model)) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refresh, 400);
  }, [refresh]);
  useRealtimeEvents(onRealtimeEvent);

  const mutation = useMutation({
    mutationFn: ({ method, path, body }) => api[method](path, body),
    onSettled: refresh,
  });
  const send = (method, path, body) => mutation.mutateAsync({ method, path, body });

  const pack = (bundleId, body) => send('post', `${BUNDLES_API}${bundleId}/pack/`, body);
  const unpack = (bundleId, content, quantity) =>
    send('post', `${BUNDLES_API}${bundleId}/unpack/`, quantity ? { content, quantity } : { content });
  const autoAssign = () => send('post', `${BUNDLES_API}auto_assign/`, { project: Number(projectId) });
  const setCaseUnit = (bundleId, caseUnit) => send('patch', `${BUNDLES_API}${bundleId}/`, { case_unit: caseUnit });
  const removeCase = (bundleId) => send('delete', `${BUNDLES_API}${bundleId}/`);
  const addCases = async (caseId, count, notes) => {
    for (let i = 0; i < count; i++) {
      await send('post', BUNDLES_API, { project: Number(projectId), bundle: caseId, quantity: 1, notes });
    }
  };

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    pending: mutation.isPending,
    pack,
    unpack,
    autoAssign,
    setCaseUnit,
    removeCase,
    addCases,
  };
};

export default usePacking;
